import React, { useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Select from "react-select";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import Modal from "../Model";
import { useAuthz } from "../../auth/useAuthz";
import PageEditor from "./PageEditor";
import PagePreview from "./PagePreview";
import {
  GET_SERVICE_PAGES, GET_SERVICE_PAGE_META, SAVE_SERVICE_PAGE, DUPLICATE_SERVICE_PAGE, DELETE_SERVICE_PAGE, PREVIEW_SERVICE_DETAIL,
} from "./graphql";
import { GET_CATALOG } from "../AdminHomeBuilder/graphql";
import { STATUS_STYLES, formatDate } from "../AdminHomeBuilder/labels";

const TAB_LABEL = { Female: "Salon", Male: "Bridal" };

const assignedText = (p) => {
  const parts = [];
  if (p.defaultFor.length) parts.push(`default for ${p.defaultFor.map((g) => TAB_LABEL[g]).join(" & ")}`);
  if (p.categoryIds.length) parts.push(`${p.categoryIds.length} categor${p.categoryIds.length === 1 ? "y" : "ies"}`);
  if (p.serviceIds.length) parts.push(`${p.serviceIds.length} service${p.serviceIds.length === 1 ? "" : "s"}`);
  return parts.length ? parts.join(" · ") : "not assigned";
};

const NewPageForm = ({ autoBlocks, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [start, setStart] = useState("auto");
  const [error, setError] = useState("");
  const [save, { loading }] = useMutation(SAVE_SERVICE_PAGE);
  const submit = async (e) => {
    e.preventDefault();
    const blocks = start === "auto" ? autoBlocks : [];
    const res = await save({ variables: { data: { name, defaultFor: [], categoryIds: [], serviceIds: [], blocks: JSON.stringify(blocks) } } });
    const out = res.data?.saveServicePage;
    if (out?.status !== "SUCCESS") { setError(out?.message || "Could not create"); return; }
    onCreated(out.id);
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">New service page</h2>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Name</span>
        <input autoFocus className="border rounded-md px-3 py-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="Facials" />
      </label>
      <div className="flex flex-col gap-2 text-sm">
        <span className="font-medium">Start from</span>
        <label className="flex items-center gap-2"><input type="radio" checked={start === "auto"} onChange={() => setStart("auto")} /> The automatic page (photos, options, how it works, notes, ratings…)</label>
        <label className="flex items-center gap-2"><input type="radio" checked={start === "empty"} onChange={() => setStart("empty")} /> An empty page</label>
      </div>
      <p className="text-xs text-gray-500">Starts as a draft. Assign it to services, categories or a tab, then publish.</p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-100">Cancel</button>
        <button type="submit" disabled={loading || !name.trim()} className="px-5 py-2 rounded-md bg-[#FBB033] font-semibold disabled:opacity-60">Create</button>
      </div>
    </form>
  );
};

/**
 * Service pages: what a customer sees after tapping a service in the app.
 * Pages are lists of blocks assigned to services, categories or a whole tab.
 */
const ServicePages = () => {
  const { can } = useAuthz();
  const canEdit = can("banners.edit");
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [lookupId, setLookupId] = useState(null);

  const pagesQuery = useQuery(GET_SERVICE_PAGES, { fetchPolicy: "network-only" });
  const metaQuery = useQuery(GET_SERVICE_PAGE_META);
  const female = useQuery(GET_CATALOG, { variables: { gender: "Female" } });
  const male = useQuery(GET_CATALOG, { variables: { gender: "Male" } });
  const [duplicate] = useMutation(DUPLICATE_SERVICE_PAGE);
  const [remove] = useMutation(DELETE_SERVICE_PAGE);
  const [lookup, lookupResult] = useLazyQuery(PREVIEW_SERVICE_DETAIL, { fetchPolicy: "network-only" });

  const pages = pagesQuery.data?.getServicePages?.pages || [];
  const autoBlocks = useMemo(() => {
    try { return JSON.parse(metaQuery.data?.getServicePageMeta?.autoBlocks || "[]"); } catch (e) { return []; }
  }, [metaQuery.data]);
  const serviceOptions = [
    ...(female.data?.getHomeBuilderCatalog?.services || []).map((s) => ({ value: s.id, label: `${s.name} · ₹${s.price} (Salon)` })),
    ...(male.data?.getHomeBuilderCatalog?.services || []).map((s) => ({ value: s.id, label: `${s.name} · ₹${s.price} (Bridal)` })),
  ];

  const doDuplicate = async (p) => {
    // eslint-disable-next-line no-alert
    const name = window.prompt("Name for the copy", `${p.name} (copy)`);
    if (!name) return;
    const res = await duplicate({ variables: { id: p.id, name } });
    const out = res.data?.duplicateServicePage;
    // eslint-disable-next-line no-alert
    if (out?.status !== "SUCCESS") { window.alert(out?.message || "Could not duplicate"); return; }
    pagesQuery.refetch();
    setOpenId(out.id);
  };
  const doDelete = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    const res = await remove({ variables: { id: p.id } });
    const out = res.data?.deleteServicePage;
    // eslint-disable-next-line no-alert
    if (out?.status !== "SUCCESS") window.alert(out?.message || "Could not delete");
    pagesQuery.refetch();
  };

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="mb-4 bg-white rounded-lg border p-4">
          <h1 className="text-xl font-semibold">Service pages</h1>
          <p className="text-sm text-gray-500">
            What customers see after tapping a service in the app: photos, wax or product options, how it works, notes, FAQs, ratings and reviews.
          </p>
        </div>

        {openId ? (
          <PageEditor
            pageId={openId}
            pages={pages}
            autoBlocks={autoBlocks}
            canEdit={canEdit}
            onBack={() => { setOpenId(null); pagesQuery.refetch(); }}
            onChanged={() => pagesQuery.refetch()}
          />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4 items-start">
            <div className="bg-white rounded-lg border">
              <div className="flex items-center justify-between p-3 border-b">
                <span className="text-sm text-gray-600">{pages.length} page{pages.length === 1 ? "" : "s"}</span>
                {canEdit && <button onClick={() => setCreating(true)} className="px-4 py-2 bg-[#FBB033] rounded-md text-sm font-semibold">+ New page</button>}
              </div>
              <ul className="divide-y">
                {pagesQuery.loading && <li className="px-3 py-6 text-center text-sm text-gray-500">Loading…</li>}
                {pages.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-3 px-3 py-3">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setOpenId(p.id)} className="font-medium hover:underline text-left">{p.name}</button>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[p.status]}`}>{p.status.toLowerCase()}</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {assignedText(p)} · {(() => { try { return JSON.parse(p.blocks).length; } catch (e) { return 0; } })()} blocks
                        {p.publishedAt ? ` · v${p.version} published ${formatDate(p.publishedAt)}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setOpenId(p.id)} className="px-3 py-1.5 rounded-md bg-gray-100 text-sm">Open</button>
                      {canEdit && <button onClick={() => doDuplicate(p)} className="px-3 py-1.5 rounded-md hover:bg-gray-100 text-sm">Duplicate</button>}
                      {canEdit && p.status !== "LIVE" && <button onClick={() => doDelete(p)} className="px-3 py-1.5 rounded-md hover:bg-red-50 text-red-600 text-sm">Delete</button>}
                    </div>
                  </li>
                ))}
                {!pagesQuery.loading && !pages.length && (
                  <li className="px-3 py-8 text-center text-sm text-gray-500">
                    No pages yet. Every service already has an automatic page built from its details; create a page to customise one category or service.
                  </li>
                )}
              </ul>
            </div>

            <aside className="bg-white border rounded-lg p-3">
              <h2 className="font-semibold text-sm mb-1">What does a customer see?</h2>
              <p className="text-xs text-gray-500 mb-2">Pick any service to see the page it gets today.</p>
              <Select
                options={serviceOptions}
                value={serviceOptions.find((o) => o.value === lookupId) || null}
                onChange={(o) => { setLookupId(o?.value || null); if (o) lookup({ variables: { serviceId: o.value } }); }}
                placeholder="Search a service"
              />
              {lookupId && (
                <div className="mt-3">
                  <PagePreview
                    detail={lookupResult.data?.previewServiceDetail?.detail}
                    loading={lookupResult.loading}
                    error={lookupResult.data?.previewServiceDetail?.status === "ERROR" ? lookupResult.data.previewServiceDetail.message : null}
                  />
                </div>
              )}
            </aside>
          </div>
        )}

        {creating && (
          <Modal onClose={() => setCreating(false)} disableOutsideClick>
            <NewPageForm autoBlocks={autoBlocks} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); pagesQuery.refetch(); setOpenId(id); }} />
          </Modal>
        )}
      </Layout>
    </div>
  );
};

export default ServicePages;
